import type { BackupHealth, EnvironmentHealthView, LiveSiteHealth, ServicesHealth } from '../../api';
import { statusFor } from '../../labels';
import type { Tone } from '../../labels';
import type { LedgerRow } from '../EnvironmentReadiness';
import { agoShort, agoSpoken } from '../status/ago';
import { infrastructureOf, latestAttemptOf, siteOf } from '../status/statusModel';
import type { SectionPath } from './operationalHealth';

/**
 * The project Overview's status strip: four facts, each a value and a few
 * short parts — never a sentence. It reads the same server derivation as
 * the rest of the product (statusModel); nothing here decides a state the
 * server did not send, and a reading that is missing is "Unknown" or
 * "Not checked", never healthy.
 *
 * Every item also carries `spoken`, the full sentence for assistive
 * technology: the visible text is short, the accessible name is not.
 */

export type StripPart = { text: string } | { ago: string };

export interface StripItem {
  key: 'site' | 'deploy' | 'infrastructure' | 'backup';
  label: string;
  value: { text: string } | { ago: string };
  tone: Tone;
  parts: StripPart[];
  /** A second line, only when something is wrong or unknown. */
  detail?: StripPart[];
  to: string;
  spoken: string;
}

export interface ReadinessSummary {
  total: number;
  ok: number;
  /** Rows reporting a warning or a failure. */
  problems: number;
  /** Rows not checked yet (the live inspection rows until the server answers). */
  unknown: number;
  /** The worst level among the rows about the host itself: agent, host, server state. */
  hostLevel: 'ok' | 'warning' | 'critical' | 'unknown';
  inspecting: boolean;
}

const HOST_ROWS = new Set(['agent', 'host', 'drift']);
const rank = { ok: 0, unknown: 1, warning: 2, critical: 3 } as const;

/** Counts the readiness rows by what they say. "Not passed" is not "failed": unknown is its own count. */
export function summariseReadiness(rows: Pick<LedgerRow, 'key' | 'level'>[], inspecting = false): ReadinessSummary {
  let hostLevel: ReadinessSummary['hostLevel'] = 'ok';
  let ok = 0;
  let problems = 0;
  let unknown = 0;
  for (const row of rows) {
    if (row.level === 'ok') ok += 1;
    else if (row.level === 'unknown') unknown += 1;
    else problems += 1;
    if (HOST_ROWS.has(row.key) && rank[row.level] > rank[hostLevel]) hostLevel = row.level;
  }
  return { total: rows.length, ok, problems, unknown, hostLevel, inspecting };
}

const levelTone: Record<string, Tone> = { ok: 'success', warning: 'warning', critical: 'danger', unknown: 'neutral' };

const siteReasonShort: Record<LiveSiteHealth['reason'], string> = {
  not_set_up: 'Not set up',
  nothing_live: 'Nothing live',
  not_monitored: 'No checks',
  not_reported: 'Not reported',
  stale: 'Checks late',
  checks_passing: '',
  checks_warning: '',
  checks_failing: '',
  maintenance: 'Maintenance',
};

function siteItem(health: EnvironmentHealthView | null | undefined, sectionPath: SectionPath): StripItem {
  const site = siteOf(health);
  const status = statusFor('siteState', site.state);
  const parts: StripPart[] = [];
  if (site.checks > 0) {
    parts.push({ text: `${site.passing}/${site.checks} checks` });
    if (site.last_checked_at) parts.push({ ago: site.last_checked_at });
    if (site.stale) parts.push({ text: 'late' });
  } else if (siteReasonShort[site.reason]) {
    parts.push({ text: siteReasonShort[site.reason] });
  }
  const detail: StripPart[] | undefined = site.failing_checks && site.failing_checks.length > 0 ? [{ text: `Failing · ${site.failing_checks.join(', ')}` }] : undefined;
  const spoken =
    site.checks > 0
      ? `Site ${status.label}. ${site.passing} of ${site.checks} checks passed${site.last_checked_at ? `, last checked ${agoSpoken(site.last_checked_at)}` : ''}${site.stale ? ', some checks are late' : ''}.${site.failing_checks && site.failing_checks.length > 0 ? ` Failing: ${site.failing_checks.join(', ')}.` : ''}`
      : `Site ${status.label}.${siteReasonShort[site.reason] ? ` ${siteReasonShort[site.reason]}.` : ''}`;
  return { key: 'site', label: 'Site', value: { text: status.label }, tone: status.tone, parts, detail, to: sectionPath('monitoring'), spoken: `${spoken} View checks.` };
}

function deployItem(health: EnvironmentHealthView | null | undefined, sectionPath: SectionPath, deploymentsElsewhere: boolean): StripItem {
  const attempt = latestAttemptOf(health);
  if (!attempt) {
    return {
      key: 'deploy',
      label: 'Last deploy',
      value: { text: deploymentsElsewhere ? 'On environment page' : 'None' },
      tone: 'neutral',
      parts: [],
      to: sectionPath('deployments'),
      spoken: deploymentsElsewhere ? 'Last deploy: deployments over SSH are listed on the environment page. View deployments.' : 'Last deploy: nothing deployed yet. View deployments.',
    };
  }
  const status = statusFor('deploymentOutcome', attempt.outcome);
  const at = attempt.deployment.completed_at ?? attempt.deployment.created_at;
  const sha = attempt.deployment.commit_sha.slice(0, 7);
  const parts: StripPart[] = [{ text: sha }, { ago: at }];
  if (attempt.failed_in_a_row && attempt.failed_in_a_row > 1) parts.push({ text: `${attempt.failed_in_a_row}× failed` });
  let detail: StripPart[] | undefined;
  if (attempt.outcome === 'failed') {
    detail = [{ text: attempt.serving === 'serving' ? 'Failed release is live' : attempt.previous_active ? 'Previous release live' : 'Never went live' }];
  } else if (attempt.outcome === 'succeeded_with_failures') {
    detail = [{ text: 'Some checks failed' }];
  } else if (attempt.outcome === 'rolled_back') {
    detail = [{ text: 'Switched back' }];
  }
  const to = sectionPath('deployments', { deployment: attempt.deployment.id });
  const spoken = `Last deploy ${status.label}. Commit ${sha}, ${agoSpoken(at)}${attempt.failed_in_a_row && attempt.failed_in_a_row > 1 ? `, ${attempt.failed_in_a_row} attempts in a row failed` : ''}.${detail ? ` ${detail.map((part) => ('text' in part ? part.text : '')).join('')}.` : ''} View deployment.`;
  return { key: 'deploy', label: 'Last deploy', value: { text: status.label }, tone: status.tone, parts, detail, to, spoken };
}

/**
 * Infrastructure: the server's level and the readiness ledger, together.
 * The server's level counts infrastructure attention items only; the ledger
 * also holds live inspection rows that are "unknown" until the server
 * answers. A warning or failure in a host row is never hidden behind a
 * healthy level, and unchecked rows are said to be unchecked.
 */
function infrastructureItem(health: EnvironmentHealthView | null | undefined, sectionPath: SectionPath, readiness: ReadinessSummary | null): StripItem {
  const infra = infrastructureOf(health);
  const services: ServicesHealth | undefined = health?.services;
  const worst = readiness && rank[readiness.hostLevel] > rank[infra.level] && (readiness.hostLevel === 'warning' || readiness.hostLevel === 'critical') ? readiness.hostLevel : infra.level;
  let value: string;
  let tone: Tone;
  if (worst === 'ok' && readiness && readiness.hostLevel === 'unknown') {
    value = 'Partly checked';
    tone = 'neutral';
  } else {
    const status = statusFor('infrastructure', worst);
    value = status.label;
    tone = levelTone[worst] ?? 'neutral';
  }
  const parts: StripPart[] = [];
  if (services && services.total > 0) parts.push({ text: `${services.reachable}/${services.total} services` });
  if (readiness && readiness.total > 0) {
    parts.push({ text: `${readiness.ok}/${readiness.total} ready` });
    if (readiness.problems > 0) parts.push({ text: `${readiness.problems} failing` });
    if (readiness.inspecting) parts.push({ text: 'checking' });
    else if (readiness.unknown > 0) parts.push({ text: `${readiness.unknown} not checked` });
  }
  // The attention area names the infrastructure issue; the strip carries the status and the numbers.
  const detail = undefined;
  const spoken = `Infrastructure ${value}.${services && services.total > 0 ? ` ${services.reachable} of ${services.total} services reachable.` : ''}${readiness && readiness.total > 0 ? ` ${readiness.ok} of ${readiness.total} readiness checks passed${readiness.problems > 0 ? `, ${readiness.problems} failing` : ''}${readiness.unknown > 0 ? `, ${readiness.unknown} not checked` : ''}.` : ''} View infrastructure.`;
  return { key: 'infrastructure', label: 'Infrastructure', value: { text: value }, tone, parts, detail, to: sectionPath('infrastructure'), spoken };
}

const backupWords: Partial<Record<BackupHealth['state'], string>> = {
  failing: 'Failing',
  stuck: 'Stuck',
  stale: 'Overdue',
  none: 'None',
  running: 'Running',
};

function backupItem(health: EnvironmentHealthView | null | undefined, sectionPath: SectionPath): StripItem {
  const backups = health?.backups;
  const to = sectionPath('recovery');
  if (!backups || backups.level === 'unknown' && !backups.last_success_at) {
    return {
      key: 'backup',
      label: 'Backup',
      value: { text: backups ? (backupWords[backups.state] ?? 'Unknown') : 'Unknown' },
      tone: 'neutral',
      parts: [],
      to,
      spoken: `Backup ${backups ? (backupWords[backups.state] ?? 'unknown') : 'unknown'}. View recovery points.`,
    };
  }
  const tone = levelTone[backups.level] ?? 'neutral';
  const withLast = backups.last_success_at && backups.state !== 'failing' && backups.state !== 'stuck' && backups.state !== 'none' && backups.state !== 'running' && backups.state !== 'stale';
  const value: StripItem['value'] = withLast ? { ago: backups.last_success_at! } : { text: backupWords[backups.state] ?? 'Unknown' };
  const parts: StripPart[] = [];
  if (!withLast && backups.last_success_at) parts.push({ text: 'Last good' }, { ago: backups.last_success_at });
  if (backups.failures_since_success > 0 && backups.state !== 'healthy') parts.push({ text: `${backups.failures_since_success}× failed` });
  if (backups.restore_test_at) {
    parts.push({ text: 'Restore test' }, { ago: backups.restore_test_at });
    if (backups.restore_test_stale) parts.push({ text: 'old' });
  } else if (backups.state === 'healthy' || backups.state === 'unverified' || backups.state === 'local_only' || backups.state === 'unscheduled' || backups.state === 'stale') {
    parts.push({ text: 'Restore not tested' });
  }
  const detail: StripPart[] = [];
  if (backups.state === 'local_only') detail.push({ text: 'No off-host copy' });
  if (backups.state === 'unscheduled') detail.push({ text: 'Not scheduled' });
  const lastSpoken = backups.last_success_at ? `Last backup ${agoSpoken(backups.last_success_at)}.` : 'No successful backup.';
  const testSpoken = backups.restore_test_at ? ` Restore test ${agoSpoken(backups.restore_test_at)}.` : ' Restore not tested.';
  return {
    key: 'backup',
    label: 'Backup',
    value,
    tone,
    parts,
    detail: detail.length > 0 ? detail : undefined,
    to,
    spoken: `Backup. ${lastSpoken}${testSpoken}${backups.failures_since_success > 0 ? ` ${backups.failures_since_success} attempts failed since.` : ''} View recovery points.`,
  };
}

export function buildStatusStrip(
  health: EnvironmentHealthView | null | undefined,
  options: { sectionPath: SectionPath; readiness?: ReadinessSummary | null; deploymentsElsewhere?: boolean },
): StripItem[] {
  return [
    siteItem(health, options.sectionPath),
    deployItem(health, options.sectionPath, Boolean(options.deploymentsElsewhere)),
    infrastructureItem(health, options.sectionPath, options.readiness ?? null),
    backupItem(health, options.sectionPath),
  ];
}

/** For tests and the strip's own rendering: the visible text of an item's parts. */
export function partsText(parts: StripPart[]): string {
  return parts.map((part) => ('text' in part ? part.text : agoShort(part.ago))).join(' · ');
}
